import { useEffect, useState } from "react";
import {
  Box, Button, Card, Table, TableBody, TableCell, TableHead, TableRow, Chip,
  Collapse, IconButton, Alert, Select, MenuItem, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import { adminApi } from "rsa/adminApi";
import SectionHeader from "../Components/SectionHeader";
import StatusChip from "../Components/StatusChip";
import AddVendorDialog from "./AddVendorDialog";

const cardSx = { borderRadius: "15px", boxShadow: "rgba(0,0,0,0.1) 0px 10px 15px -3px, rgba(0,0,0,0.05) 0px 4px 6px -2px", p: 2 };

function VendorRow({ vendor, onDecided }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setOpen((o) => !o);
    if (!docs) {
      const d = await adminApi("GET", `/admin/vendors/${vendor.id}/documents`);
      setDocs(d);
    }
  };

  const decide = async (status) => {
    setBusy(true);
    try {
      await adminApi("PATCH", `/admin/vendors/${vendor.id}/approval`, { status });
      onDecided();
    } finally { setBusy(false); }
  };

  // is_collision_care gates customer search entirely — an unflagged vendor is
  // invisible to nearby scans, so surface + fix it from here, not just the vendor app.
  const toggleCollisionCare = async () => {
    setBusy(true);
    try {
      await adminApi("PATCH", `/admin/vendors/${vendor.id}/flags`, { isCollisionCare: !vendor.is_collision_care });
      onDecided();
    } finally { setBusy(false); }
  };

  return (
    <>
      <TableRow hover>
        <TableCell><IconButton size="small" onClick={toggle}>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton></TableCell>
        <TableCell>{vendor.business_name}</TableCell>
        <TableCell>{vendor.phone}</TableCell>
        <TableCell>{vendor.capabilities?.length ? vendor.capabilities.map((c) => (
          <Chip key={c} size="small" label={c} sx={{ mr: 0.5, mb: 0.5 }} />
        )) : "—"}</TableCell>
        <TableCell>{vendor.service_radius_km} km</TableCell>
        <TableCell>{vendor.avg_rating} ★ ({vendor.total_reviews})</TableCell>
        <TableCell>
          {vendor.rsa_enabled ? (
            <Chip
              size="small"
              clickable
              disabled={busy}
              onClick={toggleCollisionCare}
              color={vendor.is_collision_care ? "success" : "default"}
              variant={vendor.is_collision_care ? "filled" : "outlined"}
              label={vendor.is_collision_care ? "Collision Care" : "Not in search"}
              title={vendor.is_collision_care
                ? "In the Collision Care program — visible to customer search. Click to remove."
                : "Not collision-care flagged — invisible to customer nearby search. Click to enable."}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">RSA off</Typography>
          )}
        </TableCell>
        <TableCell><StatusChip value={vendor.approval_status} /></TableCell>
        <TableCell>
          <Box display="flex" gap={0.5}>
            <IconButton size="small" color="success" disabled={busy || vendor.approval_status === "approved"} onClick={() => decide("approved")}>
              <CheckCircleIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" disabled={busy || vendor.approval_status === "rejected"} onClick={() => decide("rejected")}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
          <Collapse in={open}>
            <Box p={2} bgcolor="rgb(244,248,249)">
              <Typography variant="subtitle2" fontWeight={700} mb={1}>KYC documents</Typography>
              {docs == null ? (
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              ) : docs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No documents on file — informal / not-yet-onboarded vendor. Not an error state.
                </Typography>
              ) : (
                <Box display="flex" gap={2} flexWrap="wrap">
                  {docs.map((d) => (
                    <Box key={d.id} sx={{ width: 140 }}>
                      <img src={d.file_url} alt={d.doc_type} style={{ width: "100%", borderRadius: 8 }} />
                      <Typography variant="caption" display="block">{d.doc_type}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [created, setCreated] = useState(null);

  const load = async () => {
    try {
      setError("");
      setVendors(await adminApi("GET", `/admin/vendors${status ? `?status=${status}` : ""}`));
    } catch (e) { setError(e.status === 401 ? "Session expired — sign in again." : e.message); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  return (
    <Box>
      <SectionHeader
        title="Vendor Directory & KYC"
        action={
          <Box display="flex" gap={1}>
            <Select size="small" displayEmpty value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add vendor
            </Button>
          </Box>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {created && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>
          Vendor “{created.business_name}” created — they can now log in on the mobile app
          with phone {created.phone} (Service Provider → OTP).
        </Alert>
      )}
      <AddVendorDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(v) => { setCreated(v); load(); }}
      />
      <Card sx={cardSx}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Business</TableCell><TableCell>Phone</TableCell><TableCell>Capabilities</TableCell>
              <TableCell>Radius</TableCell><TableCell>Rating</TableCell><TableCell>RSA</TableCell>
              <TableCell>Status</TableCell><TableCell>Decide</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendors.length === 0 && <TableRow><TableCell colSpan={9} align="center">No vendors</TableCell></TableRow>}
            {vendors.map((v) => <VendorRow key={v.id} vendor={v} onDecided={load} />)}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
}
